import { useTranslation } from 'react-i18next'
import MemoryUsedNote from '../shared/MemoryUsedNote'
import { RichText } from './RichText'
import { renderInline } from './richInline'
import { PlanCard as Card, PlanLines as Lines, PlanMenuCards, PlanWeekendCards } from './PlanCards'
import { fmt, planLocale as locale } from './planFormat'

// VH-229 refinement: the weekly plan document, rendered in the structure of the approved cookbook page —
// header tiles, shopping list by aisle, cooking day, daily menu, training day, shelf life, weekend,
// what changes, two levers and open items. Content comes from the document (its language);
// only labels are translated here.

const GROUPS = ['dairy_protein', 'vegetables', 'fruit', 'pantry', 'equipment']
const SEVERITY_BAR = { ok: 'border-l-[#2C6E49]', warn: 'border-l-[#9C6511]', crit: 'border-l-[#A6362B]' }

function SectionHead({ eyebrow, title }) {
  return (
    <div className="flex flex-col gap-1.5 border-b-2 border-[#14181A] pb-2.5 mb-5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.13em] text-[#8B959C] font-sans">{eyebrow}</span>
      <h2 className="text-[22px] font-semibold tracking-tight font-sans text-[#14181A]">{title}</h2>
    </div>
  )
}

function Kpi({ label, value, sub }) {
  return (
    <div className="bg-white px-4 py-3 flex flex-col gap-0.5">
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[#8B959C] font-sans">{label}</span>
      <span className="font-mono text-[19px] font-medium tabular-nums tracking-tight">{value}</span>
      {sub && <span className="text-[12.5px] text-[#5B6670] font-sans">{sub}</span>}
    </div>
  )
}

function Table({ head, children, caption }) {
  return (
    <div className="overflow-x-auto border border-[#D8DCD5] rounded bg-white">
      <table className="w-full border-collapse text-[14.5px] font-sans">
        <thead>
          <tr>{head.map((h, i) => <th key={i} className="text-[10.5px] font-semibold uppercase tracking-[0.09em] text-[#8B959C] text-left px-3 py-2.5 bg-[#ECEEE9] border-b border-[#D8DCD5] whitespace-nowrap">{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      {caption && <p className="px-3 py-2 text-[13px] text-[#5B6670] font-sans">{caption}</p>}
    </div>
  )
}

const td = 'px-3 py-2 border-b border-[#D8DCD5] align-top'
const tdNum = `${td} font-mono tabular-nums whitespace-nowrap`

function Note({ title, text, crit = false }) {
  return (
    <div className={`border-l-[3px] ${crit ? 'border-[#A6362B] bg-[#F3DDD9]' : 'border-[#0E6B60] bg-[#DCEAE7]'} rounded-r px-4 py-4 flex flex-col gap-2 text-base`}>
      {title && <h4 className="text-[15px] font-semibold font-sans">{title}</h4>}
      {text}
    </div>
  )
}

export function WeeklyPlanDocument({ document: doc, weekStart }) {
  const { t, i18n } = useTranslation()
  if (!doc) return null
  const lang = i18n.language
  const header = doc.header ?? {}
  const start = new Date(`${weekStart ?? doc.week_start}T12:00:00`)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  const dateFmt = new Intl.DateTimeFormat(locale(lang), { day: 'numeric', month: 'long', year: 'numeric' })
  const meals = doc.daily_menu?.meals ?? []
  const shopping = (doc.shopping ?? []).slice().sort((a, b) => GROUPS.indexOf(a.group) - GROUPS.indexOf(b.group))
  const steps = doc.cooking_day?.steps ?? []
  const shelf = doc.shelf_life ?? {}
  const weekend = doc.weekend?.rows ?? []
  const changes = doc.changes?.rows ?? []
  const openItems = doc.open_items ?? []
  const containers = header.containers ?? 5

  return (
    <article className="bg-[#F6F7F4] text-[#14181A] font-serif text-[17px] leading-relaxed" data-testid="weekly-plan-document">
      <div className="max-w-[900px] mx-auto px-5 pt-8 pb-16">
        <p className="text-[11px] font-semibold uppercase tracking-[0.13em] text-[#8B959C] font-sans">
          {t('weeklyPlan.weekRange', { range: `${dateFmt.formatRange ? dateFmt.formatRange(start, end) : `${dateFmt.format(start)} – ${dateFmt.format(end)}`}` })}
        </p>
        <h1 className="text-[40px] leading-tight font-bold tracking-tight font-sans mt-1 mb-2">{t('weeklyPlan.title')}</h1>
        {header.intro && <p className="text-[#5B6670] text-lg max-w-[60ch]">{renderInline(header.intro)}</p>}
        <MemoryUsedNote memories={doc.memories_used} className="mt-2 font-sans" />

        <div className="grid grid-cols-[repeat(auto-fit,minmax(126px,1fr))] gap-px bg-[#D8DCD5] border border-[#D8DCD5] rounded-sm overflow-hidden mt-5" data-testid="plan-kpis">
          <Kpi label={t('weeklyPlan.kpi.protein')} value={`${fmt(header.protein_g, lang)} g`}
            sub={header.protein_g_per_kg ? t('weeklyPlan.kpi.perKg', { value: fmt(header.protein_g_per_kg, lang, 1) }) : null} />
          <Kpi label={t('weeklyPlan.kpi.kcal')} value={fmt(header.kcal_rest_day, lang)}
            sub={header.kcal_training_day ? t('weeklyPlan.kpi.trainingDay', { value: fmt(header.kcal_training_day, lang) }) : null} />
          <Kpi label={t('weeklyPlan.kpi.carbs')} value={`${fmt(header.carbs_g, lang)} g`}
            sub={header.carbs_previous_g ? t('weeklyPlan.kpi.previous', { value: fmt(header.carbs_previous_g, lang) }) : null} />
          <Kpi label={t('weeklyPlan.kpi.fiber')} value={`${fmt(header.fiber_g, lang)} g`}
            sub={header.fiber_previous_g ? t('weeklyPlan.kpi.previous', { value: fmt(header.fiber_previous_g, lang) }) : null} />
          {header.cooking_minutes ? (
            <Kpi label={t('weeklyPlan.kpi.cooking')} value={`${fmt(header.cooking_minutes, lang)} min`} sub={t('weeklyPlan.kpi.oncePerWeek')} />
          ) : null}
        </div>

        {shopping.length > 0 && (
          <section className="mt-11" data-testid="plan-shopping">
            <SectionHead eyebrow={t('weeklyPlan.shopping.eyebrow')} title={t('weeklyPlan.shopping.title')} />
            <div className="grid gap-3.5 grid-cols-[repeat(auto-fit,minmax(255px,1fr))]">
              {shopping.map((group) => (
                <Card key={group.group} label={t(`weeklyPlan.group.${group.group}`, group.group)}>
                  <Lines rows={group.items ?? []} quantityKey="quantity" />
                  {group.note && <p className="text-[13.5px] text-[#5B6670] font-sans">{renderInline(group.note)}</p>}
                </Card>
              ))}
            </div>
          </section>
        )}

        {steps.length > 0 && (
          <section className="mt-11" data-testid="plan-cooking-day">
            <SectionHead eyebrow={t('weeklyPlan.cooking.eyebrow', { weekday: header.prep_weekday || '', minutes: fmt(header.cooking_minutes, lang) })}
              title={t('weeklyPlan.cooking.title')} />
            <ol className="flex flex-col">
              {steps.map((step, i) => (
                <li key={i} className="grid grid-cols-[auto_minmax(0,1fr)] gap-4 py-4 border-b border-[#D8DCD5] first:border-t items-start">
                  <span className="font-mono text-[13px] font-semibold text-[#0E6B60] bg-[#DCEAE7] w-[29px] h-[29px] rounded-full grid place-items-center">{i + 1}</span>
                  <div className="flex flex-col gap-1.5 max-w-[62ch]">
                    <span className={`font-mono text-[12.5px] font-medium ${step.kind === 'freeze' ? 'text-[#A6362B]' : 'text-[#5B6670]'}`}>
                      {fmt(step.offset_min, lang)} min{step.timing ? ` — ${step.timing}` : ''}
                    </span>
                    <h4 className="text-[15px] font-semibold font-sans">{renderInline(step.title)}</h4>
                    <RichText text={step.text} />
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {meals.length > 0 && (
          <section className="mt-11" data-testid="plan-daily-menu">
            <SectionHead eyebrow={containers === 5 ? t('weeklyPlan.menu.eyebrowWeekdays') : t('weeklyPlan.menu.eyebrow', { count: containers })}
              title={t('weeklyPlan.menu.title')} />
            <PlanMenuCards document={doc} />
          </section>
        )}

        {(shelf.rows ?? []).length > 0 && (
          <section className="mt-11" data-testid="plan-shelf-life">
            <SectionHead eyebrow={t('weeklyPlan.shelf.eyebrow')} title={t('weeklyPlan.shelf.title')} />
            {shelf.headline_title && (
              <div className="mb-4">
                <Note crit title={shelf.headline_title} text={<RichText text={shelf.headline_text} />} />
              </div>
            )}
            <Table head={[t('weeklyPlan.shelf.component'), t('weeklyPlan.shelf.holds'), t('weeklyPlan.shelf.note')]}
              caption={shelf.caption || t('weeklyPlan.shelf.caption')}>
              {shelf.rows.map((row, i) => (
                <tr key={i}>
                  <td className={`${td} font-medium border-l-[3px] ${SEVERITY_BAR[row.severity] ?? 'border-l-transparent'}`}>{renderInline(row.component)}</td>
                  <td className={tdNum}>{row.holds}</td>
                  <td className={td}>{renderInline(row.note)}</td>
                </tr>
              ))}
            </Table>
          </section>
        )}

        {weekend.length > 0 && (
          <section className="mt-11" data-testid="plan-weekend">
            <SectionHead eyebrow={t('weeklyPlan.weekend.eyebrow')} title={t('weeklyPlan.weekend.title')} />
            <PlanWeekendCards document={doc} />
          </section>
        )}

        {(changes.length > 0 || (doc.levers ?? []).length > 0) && (
          <section className="mt-11" data-testid="plan-changes">
            <SectionHead eyebrow={t('weeklyPlan.changes.eyebrow')} title={t('weeklyPlan.changes.title')} />
            {changes.length > 0 && (
              <Table head={['', t('weeklyPlan.changes.previous'), t('weeklyPlan.changes.plan'), 'Δ']}>
                {changes.map((row) => (
                  <tr key={row.key}>
                    <td className={`${td} font-medium`}>{t(`weeklyPlan.changeKey.${row.key}`, row.key)}</td>
                    <td className={tdNum}>{row.previous}</td>
                    <td className={`${tdNum} ${row.better === false ? '' : 'text-[#2C6E49]'}`}>{row.plan}</td>
                    <td className={`${tdNum} ${row.better === false ? '' : 'text-[#2C6E49]'}`}>{row.delta}</td>
                  </tr>
                ))}
              </Table>
            )}
            {(doc.levers ?? []).length > 0 && (
              <div className="mt-4" data-testid="plan-levers">
                <Note title={doc.levers_title || t('weeklyPlan.levers.title', { count: doc.levers.length })}
                  text={doc.levers.map((lever, i) => (
                    <p key={i}><strong className="font-semibold">{lever.title}:</strong> {renderInline(lever.text)}</p>
                  ))} />
              </div>
            )}
          </section>
        )}

        {openItems.length > 0 && (
          <section className="mt-11" data-testid="plan-open-items">
            <SectionHead eyebrow={t('weeklyPlan.open.eyebrow')} title={t('weeklyPlan.open.title', { count: openItems.length })} />
            <div className="flex flex-col gap-4">
              {openItems.map((item, i) => <Note key={i} crit title={item.title} text={<RichText text={item.text} />} />)}
            </div>
          </section>
        )}
      </div>
    </article>
  )
}

export default WeeklyPlanDocument
